import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "../../context/AuthContext";
import PasswordInput from "../Auth/passwordInput";
import { Dots } from "../Loader/loader";

const firstError = (err, field) =>
  err.response?.data?.errors?.[field]?.[0] ||
  (err.response?.status === 429 ? "Չափից շատ փորձեր։ Փորձեք մի փոքր ուշ։" : "Չհաջողվեց։ Փորձեք կրկին։");

const hasArmenian = (text) => /[Ա-֏]/.test(text || "");

// Two-factor authentication (authenticator app) section of the account settings.
const TwoFactor = () => {
  const { user, setupTwoFactor, confirmTwoFactor, disableTwoFactor } = useAuth();
  const enabled = Boolean(user.two_factor_enabled);
  const needsPassword = user.has_password !== false;

  const [step, setStep] = useState("idle"); // idle | password | scan | codes | disable
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [setup, setSetup] = useState(null); // { secret, otpauth_uri }
  const [codes, setCodes] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep("idle");
    setPassword("");
    setCode("");
    setSetup(null);
    setError("");
  };

  const run = async (fn) => {
    setError("");
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      const message = firstError(err, "code");
      setError(hasArmenian(message) ? message : firstError(err, "current_password"));
    } finally {
      setBusy(false);
    }
  };

  const start = () =>
    run(async () => {
      const data = await setupTwoFactor(password);
      setSetup(data);
      setPassword("");
      setStep("scan");
    });

  const confirm = () =>
    run(async () => {
      const data = await confirmTwoFactor(code);
      setCodes(data.recovery_codes);
      setCode("");
      setSetup(null);
      setStep("codes");
    });

  const disable = () =>
    run(async () => {
      await disableTwoFactor(password, code);
      reset();
    });

  return (
    <section className="twofa">
      <h3 className="account-subtitle">Երկքայլ նույնականացում (2FA)</h3>
      <p className="auth-muted">
        Մուտք գործելիս, գաղտնաբառից բացի, պետք է մուտքագրել նաև նույնականացման հավելվածի (Google Authenticator,
        Authy և այլն) 6-անիշ կոդը։ Դա պաշտպանում է հաշիվը, եթե գաղտնաբառը գողացվի։
      </p>

      {error && <div className="auth-alert">{error}</div>}

      {step === "idle" && (
        <div className="twofa-row">
          <span className={`twofa-status ${enabled ? "on" : "off"}`}>{enabled ? "Միացված է" : "Անջատված է"}</span>
          {enabled ? (
            <button type="button" className="auth-btn outline twofa-btn" onClick={() => setStep("disable")}>
              Անջատել
            </button>
          ) : (
            <button
              type="button"
              className="auth-btn twofa-btn"
              onClick={() => (needsPassword ? setStep("password") : start())}
            >
              Միացնել
            </button>
          )}
        </div>
      )}

      {step === "password" && (
        <form
          className="auth-form"
          onSubmit={(e) => {
            e.preventDefault();
            start();
          }}
        >
          <div className="auth-field">
            <label htmlFor="twofa-password">Հաստատեք գաղտնաբառը</label>
            <PasswordInput
              id="twofa-password"
              name="twofa-password"
              autoComplete="current-password"
              canReveal={false}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="twofa-row">
            <button className="auth-btn twofa-btn" type="submit" disabled={busy || !password}>
              {busy ? <Dots /> : "Շարունակել"}
            </button>
            <button type="button" className="auth-btn outline twofa-btn" onClick={reset}>
              Չեղարկել
            </button>
          </div>
        </form>
      )}

      {step === "scan" && setup && (
        <form
          className="auth-form"
          onSubmit={(e) => {
            e.preventDefault();
            confirm();
          }}
        >
          <p>1. Սկանավորեք QR-կոդը հավելվածով (կամ մուտքագրեք բանալին ձեռքով)։</p>
          <div className="twofa-qr">
            <QRCodeSVG value={setup.otpauth_uri} size={176} level="M" />
            <code className="twofa-secret">{setup.secret.match(/.{1,4}/g).join(" ")}</code>
          </div>
          <div className="auth-field">
            <label htmlFor="twofa-code">2. Մուտքագրեք հավելվածի 6-անիշ կոդը</label>
            <input
              id="twofa-code"
              name="twofa-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          <div className="twofa-row">
            <button className="auth-btn twofa-btn" type="submit" disabled={busy || code.length < 6}>
              {busy ? <Dots /> : "Հաստատել"}
            </button>
            <button type="button" className="auth-btn outline twofa-btn" onClick={reset}>
              Չեղարկել
            </button>
          </div>
        </form>
      )}

      {step === "codes" && (
        <div className="twofa-codes-box">
          <div className="auth-alert success">2FA-ն միացված է։</div>
          <p>
            <strong>Պահպանեք այս վերականգնման կոդերը անվտանգ տեղում։</strong> Յուրաքանչյուրը աշխատում է մեկ անգամ,
            եթե կորցնեք հեռախոսը։ Դրանք այլևս չեն ցուցադրվի։
          </p>
          <ul className="twofa-codes">
            {codes.map((c) => (
              <li key={c}>
                <code>{c}</code>
              </li>
            ))}
          </ul>
          <button type="button" className="auth-btn twofa-btn" onClick={reset}>
            Պահպանեցի
          </button>
        </div>
      )}

      {step === "disable" && (
        <form
          className="auth-form"
          onSubmit={(e) => {
            e.preventDefault();
            disable();
          }}
        >
          {needsPassword && (
            <div className="auth-field">
              <label htmlFor="twofa-off-password">Գաղտնաբառ</label>
              <PasswordInput
                id="twofa-off-password"
                name="twofa-off-password"
                autoComplete="current-password"
                canReveal={false}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          )}
          <div className="auth-field">
            <label htmlFor="twofa-off-code">Հավելվածի կոդը կամ վերականգնման կոդը</label>
            <input
              id="twofa-off-code"
              name="twofa-off-code"
              autoComplete="one-time-code"
              maxLength={16}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
          <div className="twofa-row">
            <button className="auth-btn twofa-btn" type="submit" disabled={busy || !code || (needsPassword && !password)}>
              {busy ? <Dots /> : "Անջատել 2FA-ն"}
            </button>
            <button type="button" className="auth-btn outline twofa-btn" onClick={reset}>
              Չեղարկել
            </button>
          </div>
        </form>
      )}
    </section>
  );
};

export default TwoFactor;
